import { useState } from 'react';
import { MoreHoriz } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui/tooltip';
import { useTheme } from 'components/core/theme-provider';
import { cn } from 'lib/utils';
import { SidebarAgentsNavIcon, SidebarProjectsNavIcon } from './icons/sidebar-rail-icons';

export type AgentGroupBy = 'projects' | 'agents';

const SIDEBAR_AGENTS_GROUP_BY_KEY = 'sidebar-agents-group-by';

export function readAgentGroupBy(): AgentGroupBy {
  try {
    return localStorage.getItem(SIDEBAR_AGENTS_GROUP_BY_KEY) === 'projects' ? 'projects' : 'agents';
  } catch {
    return 'agents';
  }
}

export function storeAgentGroupBy(value: AgentGroupBy) {
  try {
    localStorage.setItem(SIDEBAR_AGENTS_GROUP_BY_KEY, value);
  } catch {
    // Preference is optional when storage is blocked.
  }
}

type AgentsGroupMenuProps = {
  value: AgentGroupBy;
  onChange: (value: AgentGroupBy) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  triggerClassName?: string;
};

const menuItemClass =
  'h-10 gap-1 overflow-visible rounded-xs px-2.5 py-1 text-sm font-normal leading-5 md:h-8 hover:bg-true-white data-[highlighted]:bg-true-white focus:bg-true-white dark:hover:bg-popover dark:data-[highlighted]:bg-popover dark:focus:bg-popover';

const selectedItemClass =
  'bg-grey-50 hover:bg-grey-50 data-[highlighted]:bg-grey-50 focus:bg-grey-50 dark:bg-sidebar-pill-dark dark:hover:bg-sidebar-pill-dark dark:data-[highlighted]:bg-sidebar-pill-dark dark:focus:bg-sidebar-pill-dark';

export function AgentsGroupMenu({
  value,
  onChange,
  open,
  onOpenChange,
  triggerClassName,
}: AgentsGroupMenuProps) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const textColor = theme === 'dark' ? 'text-true-white' : 'text-grey-900';
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const menuLabel = t('GROUP_BY_AGENT_OR_PROJECT', { defaultValue: 'Group by agent or project' });

  const options: { id: AgentGroupBy; label: string }[] = [
    { id: 'projects', label: t('GROUP_BY_PROJECTS', { defaultValue: 'Group By Projects' }) },
    { id: 'agents', label: t('GROUP_BY_AGENTS', { defaultValue: 'Group by Agents' }) },
  ];

  return (
    <TooltipProvider delayDuration={300}>
      <DropdownMenu modal={false} open={open} onOpenChange={onOpenChange}>
      <Tooltip open={!open && tooltipOpen} onOpenChange={setTooltipOpen}>
        <DropdownMenuTrigger asChild>
          <TooltipTrigger asChild>
            <button
              type="button"
              data-buddy-tour-target="agents-group"
              aria-label={menuLabel}
              className={cn(triggerClassName, 'data-[state=open]:opacity-100')}
              onPointerDown={(event) => event.stopPropagation()}
              onClick={(event) => event.stopPropagation()}
            >
              <MoreHoriz width={16} height={16} strokeWidth={1.5} />
            </button>
          </TooltipTrigger>
        </DropdownMenuTrigger>
        <TooltipContent
          side="top"
          sideOffset={6}
          className={cn(
            'rounded-nav border border-grey-100 bg-true-white px-3 py-1.5 text-sm font-normal leading-heading shadow-primary-soft dark:border-sidebar-border dark:bg-sidebar',
            textColor
          )}
        >
          {menuLabel}
        </TooltipContent>
      </Tooltip>
      <DropdownMenuContent
        side="right"
        align="start"
        sideOffset={8}
        className="w-max gap-1 overflow-visible rounded-field border-hairline border-grey-300 bg-true-white px-3 py-3 shadow-popover-light dark:border-border dark:bg-popover dark:shadow-md"
      >
        {options.map((option) => {
          const selected = value === option.id;
          return (
            <DropdownMenuItem
              key={option.id}
              data-selected={selected ? 'true' : 'false'}
              className={cn(menuItemClass, textColor, selected && selectedItemClass)}
              onSelect={() => onChange(option.id)}
            >
              <span className="flex min-w-0 items-center gap-1 opacity-80">
                {option.id === 'projects' ? (
                  <SidebarProjectsNavIcon width={20} height={20} className="shrink-0" />
                ) : (
                  <SidebarAgentsNavIcon width={22} height={22} className="shrink-0" />
                )}
                <span className="truncate">{option.label}</span>
              </span>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
      </DropdownMenu>
    </TooltipProvider>
  );
}
